"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth.store";
import { useToastStore } from "@/store/toast.store";
import { cartService } from "@/services/cart.service";
import type {
  ApiCart,
  CartFees,
  PromoValidationResult,
  PromoCodeListItem,
  CartDraft,
  AddToCartPayload,
  UpdateCartItemPayload,
  CalculateCartPayload,
  ListPromoCodesParams,
  ValidatePromoPayload,
  ValidatePromoBeforeOrderPayload,
  CreateDraftPayload,
  ConfirmOrderPayload,
} from "@/types";
import { trackAddToCart, type AddToCartTracking } from "@/services/analytics/commerce";

const CART_KEYS = {
  all: ["carts"] as const,
  store: (storeUlid: string) => ["cart", storeUlid] as const,
  draft: (storeUlid: string) => ["cart-draft", storeUlid] as const,
};

// ── All carts ──────────────────────────────────────────────────

export function useAllCarts() {
  const user = useAuthStore((s) => s.user);

  return useQuery<ApiCart[]>({
    queryKey: CART_KEYS.all,
    queryFn: () => cartService.getAllCarts(),
    enabled: !!user,
    staleTime: 30 * 1000,
  });
}

// ── Store cart ─────────────────────────────────────────────────

export function useStoreCart(storeUlid: string | null) {
  const user = useAuthStore((s) => s.user);

  return useQuery<ApiCart>({
    queryKey: CART_KEYS.store(storeUlid ?? ""),
    queryFn: () => cartService.getStoreCart(storeUlid!),
    enabled: !!user && !!storeUlid,
    staleTime: 30 * 1000,
  });
}

// ── Add to cart ────────────────────────────────────────────────

/**
 * Les variables d'un ajout au panier : la charge utile de l'API, plus ce que la
 * mesure d'audience attend (nom, prix). `tracking` ne part jamais vers l'API —
 * elle refuse toute propriété inconnue (« property X should not exist ») ; la
 * charge utile est reconstruite champ par champ.
 */
export type AddToCartVariables = AddToCartPayload & { tracking?: AddToCartTracking };

function apiPayload(v: AddToCartVariables): AddToCartPayload {
  return {
    product_ulid: v.product_ulid,
    quantity: v.quantity,
    store_ulid: v.store_ulid,
    ...(v.addons ? { addons: v.addons } : {}),
  };
}

export function useAddToCart() {
  const queryClient = useQueryClient();
  const toast = useToastStore();

  return useMutation({
    mutationFn: (variables: AddToCartVariables) =>
      cartService.addToCart(apiPayload(variables)),
    onSuccess: (_data, variables) => {
      if (variables.tracking) {
        trackAddToCart(variables.product_ulid, variables.quantity, variables.store_ulid, variables.tracking);
      }
      queryClient.invalidateQueries({ queryKey: CART_KEYS.all });
      queryClient.invalidateQueries({ queryKey: CART_KEYS.store(variables.store_ulid) });
      // Stay on the current page — the user opens the cart via the navbar icon.
      toast.show("Ajouté au panier", { label: "Voir le panier", href: `/cart/${variables.store_ulid}` });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Impossible d'ajouter au panier";
      toast.show(message);
    },
  });
}

// ── Update cart item quantity ──────────────────────────────────

export function useUpdateCartItem() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      storeUlid,
      cartItemId,
      payload,
    }: {
      storeUlid: string;
      cartItemId: string;
      payload: UpdateCartItemPayload;
    }) => cartService.updateCartItem(storeUlid, cartItemId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: CART_KEYS.all });
      queryClient.invalidateQueries({ queryKey: CART_KEYS.store(variables.storeUlid) });
    },
  });
}

// ── Remove cart item ───────────────────────────────────────────

export function useRemoveCartItem() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      storeUlid,
      cartItemId,
    }: {
      storeUlid: string;
      cartItemId: string;
    }) => cartService.removeCartItem(storeUlid, cartItemId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: CART_KEYS.all });
      queryClient.invalidateQueries({ queryKey: CART_KEYS.store(variables.storeUlid) });
    },
  });
}

// ── Clear store cart ───────────────────────────────────────────

export function useClearStoreCart() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storeUlid: string) =>
      cartService.clearStoreCart(storeUlid),
    onSuccess: (_data, storeUlid) => {
      queryClient.invalidateQueries({ queryKey: CART_KEYS.all });
      queryClient.setQueryData(CART_KEYS.store(storeUlid), null);
    },
  });
}

// ── Clear all carts ────────────────────────────────────────────

export function useClearAllCarts() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => cartService.clearAllCarts(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CART_KEYS.all });
    },
  });
}

// ── Calculate cart fees ────────────────────────────────────────

export function useCalculateCart() {
  const user = useAuthStore((s) => s.user);

  return useMutation<CartFees, Error, { storeUlid: string; payload: CalculateCartPayload }>({
    mutationFn: ({ storeUlid, payload }) =>
      cartService.calculateCart(storeUlid, payload),
  });
}

// ── Validate promo code ────────────────────────────────────────

export function useValidatePromo() {
  const user = useAuthStore((s) => s.user);

  return useMutation<PromoValidationResult, Error, ValidatePromoPayload>({
    mutationFn: (payload) => cartService.validatePromo(payload),
  });
}

export function useValidatePromoBeforeOrder() {
  const user = useAuthStore((s) => s.user);

  return useMutation<PromoValidationResult, Error, ValidatePromoBeforeOrderPayload>({
    mutationFn: (payload) => cartService.validatePromoBeforeOrder(payload),
  });
}

export function usePublicPromoCodes(params: ListPromoCodesParams = {}) {
  const user = useAuthStore((s) => s.user);
  return useQuery<PromoCodeListItem[]>({
    queryKey: ["promo-codes", params],
    queryFn: () => cartService.listPublicPromoCodes(params),
    enabled: !!user,
    staleTime: 60 * 1000,
  });
}

// ── Cart draft ─────────────────────────────────────────────────

export function useCartDraft(storeUlid: string | null) {
  const user = useAuthStore((s) => s.user);

  return useQuery<CartDraft>({
    queryKey: CART_KEYS.draft(storeUlid ?? ""),
    queryFn: () => cartService.getDraft(storeUlid!),
    enabled: !!user && !!storeUlid,
    staleTime: 60 * 1000,
  });
}

// ── Create / update draft ──────────────────────────────────────

export function useCreateDraft() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateDraftPayload) =>
      cartService.createDraft(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: CART_KEYS.draft(variables.store_ulid) });
    },
  });
}

// ── Cancel draft ───────────────────────────────────────────────

export function useCancelDraft() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storeUlid: string) =>
      cartService.cancelDraft(storeUlid),
    onSuccess: (_data, storeUlid) => {
      queryClient.invalidateQueries({ queryKey: CART_KEYS.draft(storeUlid) });
    },
  });
}

// ── Confirm / place order ──────────────────────────────────────

export function useConfirmOrder() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      storeUlid,
      payload,
    }: {
      storeUlid: string;
      payload: ConfirmOrderPayload;
    }) => cartService.confirmOrder(storeUlid, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: CART_KEYS.all });
      queryClient.invalidateQueries({ queryKey: CART_KEYS.store(variables.storeUlid) });
    },
  });
}

// ── Convenience: total item count across all carts ─────────────

export function useCartItemCount(): number {
  const { data: carts } = useAllCarts();
  if (!carts) return 0;
  return carts.reduce(
    (total, cart) =>
      total + cart.items.reduce((sum, item) => sum + item.quantity, 0),
    0
  );
}
